// composables/useMensajesWhatsapp.js
// Estado real de los mensajes de WhatsApp de un pedido.
//
// Combina dos fuentes:
//   1. GET al abrir el modal  → estado actual (histórico)
//   2. WebSocket              → cambios que reporta Meta después
//
// El segundo importa tanto como el primero: el "leído" puede llegar horas
// después del envío, cuando ya nadie está mirando la pantalla.

import { ref, computed, onUnmounted } from "vue"
import { getMensajesWhatsapp } from "@/utils/api_backend"
import { suscribirWhatsapp } from "@/utils/ws_whatsapp"

// ── Catálogo de presentación ────────────────────────────────────────────────
// Un solo lugar donde se decide cómo se ve cada estado, para que el badge del
// modal y cualquier otra vista no se contradigan.
export const ESTATUS_WA = {
  PENDIENTE: {
    label: "En cola",
    icono: "bi-hourglass-split",
    clase: "wa-pendiente",
    ayuda: "El mensaje está encolado, todavía no sale del servidor.",
  },
  ACEPTADO: {
    label: "Aceptado",
    icono: "bi-cloud-arrow-up",
    clase: "wa-aceptado",
    ayuda: "WhatsApp recibió el mensaje. Aún no confirma la entrega.",
  },
  ENVIADO: {
    label: "Enviado",
    icono: "bi-check",
    clase: "wa-enviado",
    ayuda: "WhatsApp ya lo despachó hacia el teléfono del cliente.",
  },
  ENTREGADO: {
    label: "Entregado",
    icono: "bi-check-all",
    clase: "wa-entregado",
    ayuda: "Llegó al teléfono del cliente.",
  },
  LEIDO: {
    label: "Leído",
    icono: "bi-check-all",
    clase: "wa-leido",
    ayuda: "El cliente abrió el mensaje.",
  },
  FALLIDO: {
    label: "Falló",
    icono: "bi-x-circle-fill",
    clase: "wa-fallido",
    ayuda: "No se pudo entregar al cliente.",
  },
  RECHAZADO: {
    label: "Rechazado",
    icono: "bi-exclamation-triangle-fill",
    clase: "wa-fallido",
    ayuda: "WhatsApp rechazó el envío; el mensaje nunca salió.",
  },
}

export function useMensajesWhatsapp() {
  const mensajes = ref([])
  const cargando = ref(false)
  const numeroPedido = ref(null)

  let baja = null

  /** Carga el histórico y engancha el WebSocket. */
  async function cargar(numero) {
    const mismoPedido = numeroPedido.value === numero
    numeroPedido.value = numero
    cargando.value = true

    // Escuchar ANTES de pedir el histórico: si se suscribe después, los
    // eventos que lleguen durante la petición se pierden.
    escuchar()

    try {
      const traidos = await getMensajesWhatsapp(numero)
      // Fusionar en vez de reemplazar: entre que se dispara el GET y que
      // responde pueden haber llegado eventos por WebSocket, y sobrescribir
      // el arreglo los tiraría.
      mensajes.value = mismoPedido ? fusionar(traidos, mensajes.value) : traidos
    } catch (e) {
      console.error("[whatsapp] No se pudo cargar el histórico:", e)
      if (!mismoPedido) mensajes.value = []
    } finally {
      cargando.value = false
    }
  }

  /** Une histórico y estado en memoria por id, sin perder ninguno. */
  function fusionar(traidos, enMemoria) {
    const porId = new Map(traidos.map((m) => [m.id, m]))
    enMemoria.forEach((m) => {
      porId.set(m.id, { ...(porId.get(m.id) || {}), ...m })
    })
    return [...porId.values()].sort(
      (a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion)
    )
  }

  /** Suscripción idempotente: llamar dos veces no duplica listeners. */
  function escuchar() {
    if (baja) return
    baja = suscribirWhatsapp((evento) => {
      if (evento.tipo !== "estatus") return
      const entrante = evento.mensaje
      // El canal es global; ignorar lo que no sea de este pedido.
      if (entrante.numero_pedido !== numeroPedido.value) return

      const idx = mensajes.value.findIndex((m) => m.id === entrante.id)
      if (idx === -1) {
        mensajes.value = [entrante, ...mensajes.value]
      } else {
        // Fusionar: el evento del WebSocket no trae todos los campos del
        // serializer REST (teléfono, quién lo mandó), y perderlos vaciaría
        // la tarjeta al primer cambio de estatus.
        mensajes.value[idx] = { ...mensajes.value[idx], ...entrante }
      }
    })
  }

  function dejarDeEscuchar() {
    if (baja) {
      baja()
      baja = null
    }
  }

  function limpiar() {
    mensajes.value = []
    numeroPedido.value = null
    dejarDeEscuchar()
  }

  /** Último mensaje de un tipo — es el que refleja el estado vigente. */
  function ultimoDe(tipo) {
    return mensajes.value.find((m) => m.tipo === tipo) || null
  }

  const ultimaFactura = computed(() => ultimoDe("FACTURA"))
  const ultimasGuias = computed(() => ultimoDe("GUIAS"))

  onUnmounted(dejarDeEscuchar)

  return {
    mensajes,
    cargando,
    cargar,
    limpiar,
    ultimoDe,
    ultimaFactura,
    ultimasGuias,
  }
}

// ── Helpers de presentación ─────────────────────────────────────────────────

export function presentacion(estatus) {
  return (
    ESTATUS_WA[estatus] || {
      label: estatus || "—",
      icono: "bi-question-circle",
      clase: "wa-pendiente",
      ayuda: "",
    }
  )
}

/** ¿Sigue en tránsito? Sirve para mostrar spinner y bloquear reenvíos. */
export function enTransito(mensaje) {
  return !!mensaje && ["PENDIENTE", "ACEPTADO"].includes(mensaje.estatus)
}

export function fallo(mensaje) {
  return !!mensaje && ["FALLIDO", "RECHAZADO"].includes(mensaje.estatus)
}

/** Texto del error listo para mostrar, sin repetir título y detalle iguales. */
export function textoError(mensaje) {
  if (!mensaje) return ""
  const partes = [mensaje.error_titulo, mensaje.error_detalle].filter(Boolean)
  if (partes.length === 2 && partes[0] === partes[1]) return partes[0]
  return partes.join(" — ")
}

export function formatFechaHora(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}
